import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { systemError } from 'feature-components/system-error-alert';
import {
  usePostTestInstanceResponseMarker,
  useTestInstanceResponseMarkers
} from 'services/assist-api/entities/test-instance-response-marker/hooks';
import type {
  PostTestInstanceResponseMarker,
  TestInstanceResponseMarkerWithCustomerUser
} from 'services/assist-api/entities/test-instance-response-marker/types';
import { AssistQueryClient } from 'services/assist-api/query-client';

export default function useAllocationHandler(
  responseId: number,
  currentOrganisationUserId: number,
  setCurrentUserMarkerAndActiveMarkerIds: (markerId: number | undefined) => void
): {
  assignCurrentUserToMark: () => Promise<void>;
  isAllocating: boolean;
} {
  const [isLoading, setIsLoading] = useState(false);

  const postTestInstanceResponseMarker = usePostTestInstanceResponseMarker({
    onSuccess: ({ data }) => {
      toast.success(`Allocated current marker to response #${data.json.testInstanceResponse.id}.`);
    },
    onError: (error) => {
      toast.error(`Failed to allocate current marker to response: ${error.message}`);
    }
  });

  /**
   * Creates a marker (of the response) and sets them to the current and active marker.
   */
  const createAndAssignTestInstanceResponseMarker = useCallback(
    async (testInstanceResponseId: number, organisationUserId: number) => {
      const markerToPost: PostTestInstanceResponseMarker = {
        testInstanceResponseId,
        customerUserId: organisationUserId
      };
      postTestInstanceResponseMarker.mutate(markerToPost, {
        onSuccess: (data) => {
          const newlyCreatedMarkerId = data.data.json.id;
          // After creating a response marker for the current user, set the newly created marker as
          // the current and active marker:
          setCurrentUserMarkerAndActiveMarkerIds(newlyCreatedMarkerId);
        }
      });
    },
    [postTestInstanceResponseMarker, setCurrentUserMarkerAndActiveMarkerIds]
  );

  /**
   * Fetches the current markers of the response.
   */
  const responseMarkersQuery =
    useTestInstanceResponseMarkers<TestInstanceResponseMarkerWithCustomerUser>(
      {
        filters: { testInstanceResponse: responseId },
        groups: ['testinstanceresponsemarker-user'],
        include: 'users'
      },
      { enabled: !!responseId }
    );

  /**
   * Checks if current organisation user is a marker of the response. We need to refetch this info
   * before save for independent markers in case they were allocated/deallocated during the time it
   * took to mark the response and click a save button.
   */
  const checkIfUserIsAMarkerOfThisResponse = useCallback(async (): Promise<boolean> => {
    // Clear the cache to ensure that the markers of the response are refetched from the API.
    await AssistQueryClient.invalidateQueries(['test-instance-response-markers']);

    // Trigger the markers query to run again now that the cache has been cleared:
    const latestMarkersQuery = await responseMarkersQuery.refetch();

    return !!latestMarkersQuery.data?.json.find((m) => m.user.id === currentOrganisationUserId);
  }, [currentOrganisationUserId, responseMarkersQuery]);

  /**
   * Checks if the current organisation user is a marker of the response and allocate them if not,
   * then set that user as the current and active marker of the mark.
   */
  async function assignCurrentUserToMark(): Promise<void> {
    if (isLoading) return;
    setIsLoading(true);
    const isMarkerOfResponse = await checkIfUserIsAMarkerOfThisResponse();
    if (!isMarkerOfResponse) {
      // Allocate the current user to the response + set them as the current and active marker:
      await createAndAssignTestInstanceResponseMarker(responseId, currentOrganisationUserId);
    } else {
      // If already allocated, just set the independent current user marker to the active marker.
      // Note: since this function may be called twice (i.e. if the outer form is submitted and
      // this function is called on submit, then the form is submitted again, we should refetch the
      // response markers again from the API to ensure that newly allocated markers are included and
      // can be set as the currentUserMarkerId and activeMarkerId:
      const latestMarkersQuery = await responseMarkersQuery.refetch();
      const currentUserMarkerId = latestMarkersQuery.data?.json.find(
        (m) => m.user.id === currentOrganisationUserId
      )?.id;
      if (currentUserMarkerId) {
        setCurrentUserMarkerAndActiveMarkerIds(currentUserMarkerId);
        toast.success('Set current user as the active marker of this mark.');
      } else {
        toast.error(systemError(`No current user marker found for response #${responseId}.`));
      }
    }
    setIsLoading(false);
  }

  return {
    assignCurrentUserToMark,
    isAllocating:
      isLoading || responseMarkersQuery.isLoading || postTestInstanceResponseMarker.isLoading
  };
}
